import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import RazorPayPG from "views/PGComponents/RazorPayPG";
import RazorPayPGSubscription from "views/PGComponents/RazorPayPGSubscription";
import RazorPayPGSubscriptionUpdate from "views/PGComponents/RazorPayPGSubscriptionUpdate";
import InstamojoPG from "views/PGComponents/InstamojoPG";
import AuthorizeDotNetPG from "views/PGComponents/AuthorizeDotNetPG";
import PayuPG from "views/PGComponents/PayuPG";
import CashFreePG from "views/PGComponents/CashFreePG";
import { Spinner, Modal } from "react-bootstrap";
import { globalVars } from "Config";

const ProcessingPage = () => {
  const [searchParams] = useSearchParams();
  const [pgDetails, setPGDetails] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { PAYMENTS_SERVICE_URL } = globalVars;
  const queryString = searchParams.toString();
  const queryParams = useMemo(() => Object.fromEntries(searchParams.entries()),
    [searchParams]
  );
  const lastQueryRef = useRef("");

  useEffect(() => {
    if (lastQueryRef.current === queryString) return;
    lastQueryRef.current = queryString;

    let subscriptionData: any = "";

    if (queryParams?.auto_renewal || queryParams?.update_subscription) {
      try {
        const decoded = decodeURIComponent(queryParams.data.replace(/\+/g, ' '));
        subscriptionData = JSON.parse(decoded);
      } catch (err) {
        console.error('Failed to parse subscription data:', err);
      }
    }

    const clientReferenceId = localStorage.getItem("clientRefId") || "";
    const clientRefId = queryParams.auto_renewal ? subscriptionData?.client_reference_id : queryParams?.client_reference_id;

    if (clientRefId && clientReferenceId === clientRefId) {
      setShowErrorModal(true);
      return;
    }

    const controller = new AbortController();

    const onLoad = async () => {
      try {
        if (clientRefId) {
         localStorage.setItem("clientRefId", clientRefId);
        }
        const result = await axios.post(`${PAYMENTS_SERVICE_URL}/open/api/v1/payment-orders?${queryString}`, {}, { signal: controller.signal });
        if (result?.data?.success) {
          const { payload } = result.data;
          setPGDetails(payload)
        }
      } catch (e: any) {
        if (e?.response && e?.response?.data) {
          const duplicateRef = e?.response?.data?.message?.includes("payment order already exists for clientReferenceId") || "";
          if (duplicateRef) {
            setShowErrorModal(true);
            return;
          }
        }
        console.error(e);
      }
    };

    const onLoadRecurring = async () => {
      try {
        if (clientRefId) {
          localStorage.setItem("clientRefId", clientRefId);
        }
        const result = await axios.post(`${PAYMENTS_SERVICE_URL}/open/api/v1/subscription/preSubscribe`, subscriptionData, { signal: controller.signal });
        if (result?.data?.success) {
          const payload  = result.data;
          setPGDetails(payload)
        }
      } catch (e: any) {
        if (e?.response && e?.response?.data) {
          const duplicateRef = e?.response?.data?.message?.includes("payment order already exists for clientReferenceId") || "";
          if (duplicateRef) {
            setShowErrorModal(true);
            return;
          }
        }
        console.error(e);
      }
    };

    const onLoadRecurringUpdate = async () => {
      try {
        if (clientRefId) {
          localStorage.setItem("clientRefId", clientRefId);
        }
        const result = await axios.post(`${PAYMENTS_SERVICE_URL}/open/api/v1/subscription/preUpdatePaymentMethod`, subscriptionData, { signal: controller.signal });
        if (result?.data?.success) {
          const payload  = result.data;
          setPGDetails(payload)
        }
      } catch (e: any) {
        if (e?.response && e?.response?.data) {
          const duplicateRef = e?.response?.data?.message?.includes("payment order already exists for clientReferenceId") || "";
          if (duplicateRef) {
            setShowErrorModal(true);
            return;
          }
        }
        console.error(e);
      }
    }

    if (queryParams?.update_subscription && subscriptionData) {
      onLoadRecurringUpdate();
    } else if (queryParams?.auto_renewal && subscriptionData) {
      onLoadRecurring();
    } else {
      onLoad();
    }
    return () => controller.abort();

  }, [queryString, PAYMENTS_SERVICE_URL, queryParams?.client_reference_id, queryParams?.auto_renewal, queryParams?.data, queryParams?.update_subscription]);

  const showPG = useCallback((pgD: { gateway_type?: string; sdk_invocation_details?: object; http_invocation_details?: object; subscription_detail?: { gateway_subscription_id?: string } & object }) => {

    const { gateway_type, sdk_invocation_details, http_invocation_details, subscription_detail } = pgD
    let PG = null
    switch (gateway_type) {
      case "RAZORPAY":
        if (queryParams?.update_subscription && subscription_detail?.gateway_subscription_id) {
          PG = <RazorPayPGSubscriptionUpdate
            data={http_invocation_details}
            subscription={subscription_detail}
            queryParams={queryParams}
          />
        } else if(queryParams?.auto_renewal && subscription_detail?.gateway_subscription_id) {
          PG = <RazorPayPGSubscription
            data={http_invocation_details}
            subscription={subscription_detail}
            queryParams={queryParams}
          />
        } else {
          PG = <RazorPayPG
            data={sdk_invocation_details}
            queryParams={queryParams}
          />
        }
        break
      case "INSTAMOJO":
        PG = <InstamojoPG
          data={http_invocation_details}
          queryParams={queryParams}
        />
        break;
      case "PAYU":
        PG = <PayuPG
          data={http_invocation_details}
          queryParams={queryParams}
        />
        break;
      case "AUTHORIZENET":
        PG = <AuthorizeDotNetPG
          data={http_invocation_details}
          queryParams={queryParams}
        />
        break;
      case "CASHFREE":
        PG = <CashFreePG
          data={sdk_invocation_details}
          queryParams={queryParams}
        />
        break;
      default:
        PG = <div className="spinner-container">
          <Spinner animation="border" variant="primary" />
          <div>Please don't hit back button or refresh</div>
        </div>;
    }
    return PG
  }, [queryParams])

  const renderErrorModal = () => (
    <Modal show={showErrorModal} backdrop="static" centered>
      <Modal.Header>
        <Modal.Title><div>Payment session reset.<p style={{ "fontSize": "16px", "margin": 0, "fontWeight": "normal" }}>No charges have been made.</p></div></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>It looks like the page was refreshed while your payment session was loading. Your session has been reset - no payment was attempted and no card details were submitted.</p>
        <p>To avoid this, please don't refresh or press the back button while the payment is processing.</p>
      </Modal.Body>
      {/* <Modal.Footer>
         <Button variant="primary" onClick={() => setShowErrorModal(false)}>
          OK
        </Button> 
      </Modal.Footer> */}
    </Modal>
  );

  return (
    <>
      {renderErrorModal()}
      {showPG(pgDetails)}
    </>
  );
};

export default ProcessingPage;
